import { useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller } from 'react-hook-form';
import ReactSelect from 'react-select';
import { useCreateCourseForm } from '../../../../hooks/forms/moodle/useCreateCourseForm';
import { useGetMoodleCourses } from '../../../../hooks/queries/moodle/useGetMoodleCourses';
import { useGetConfiguredCourses } from '../../../../hooks/queries/moodle/useGetConfiguredCourses';
import { useGetCourseExclusions } from '../../../../hooks/queries/moodle/useGetCourseExclusions';
import ExcludeUsersModal from '../../../../components/Moodle/ExcludeUsersModal';
import EditCourseModal from '../../../../components/Moodle/EditCourseModal';
import { useRemoveExclusion } from '../../../../hooks/queries/moodle/useRemoveExclusion';
import { useGetOfficeLocations } from '../../../../hooks/queries/masterdata/useGetOfficeLocations';
import { useAutoLinkMoodleMappings } from '../../../../hooks/queries/moodle/useAutoLinkMoodleMappings';
import PaginationComponent from '../../../../components/Pagination/Pagination';

const RECURRENCE_OPTIONS = [
  { label: 'Yearly', value: 'YEARLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Once', value: 'ONCE' },
];

const COURSE_TABLE_COLUMNS = [
  { id: 'courseNameSnapshot', label: 'Course Name' },
  { id: 'year', label: 'Year' },
  { id: 'recurrence', label: 'Recurrence' },
  { id: 'launchDate', label: 'Launch Date' },
  { id: 'endDate', label: 'End Date' },
  { id: 'graceDays', label: 'Grace Days' },
  { id: 'freezeDays', label: 'Freeze Days' },
  { id: 'isMandatory', label: 'Mandatory' },
  { id: 'isActive', label: 'Active' },
  { id: 'warningNotificationDays', label: 'Warning Days' },
  { id: 'actions', label: '' },
];

const EXCLUSION_TABLE_COLUMNS = [
  { id: 'employeeId', label: 'Employee ID' },
  { id: 'courseId', label: 'Course ID' },
  { id: 'reason', label: 'Reason' },
  { id: 'actions', label: '' },
];

// Moodle returns Unix timestamps (seconds). Convert to YYYY-MM-DD for date inputs.
const toDateStr = (val) => {
  if (!val) return '';
  const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const Courses = () => {
  const [expanded, setExpanded] = useState(false);
  const [excludeModalOpen, setExcludeModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourseForExclusions, setSelectedCourseForExclusions] =
    useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { mutate: autoLinkMappings, isPending: isAutoLinking } =
    useAutoLinkMoodleMappings();

  const { courses, loading: coursesLoading } = useGetMoodleCourses();

  const {
    configuredCourses,
    totalPages,
    loading: tableLoading,
  } = useGetConfiguredCourses({ page, size: pageSize });

  const { configuredCourses: allConfiguredCourses } = useGetConfiguredCourses({
    page: 0,
    size: 1000,
  });
  const { exclusions, loading: exclusionsLoading } = useGetCourseExclusions(
    selectedCourseForExclusions?.id,
  );

  const { mutate: removeExclusion, isPending: isRemoving } =
    useRemoveExclusion();
  const { locations, loading: locationsLoading } = useGetOfficeLocations();

  const { form, onSubmit, isPending } = useCreateCourseForm(() =>
    setExpanded(false),
  );

  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const courseOptions = courses.map((c) => ({
    label: c.fullName,
    value: c.id,
    shortName: c.shortName,
    startDate: toDateStr(c.startDate),
    endDate: toDateStr(c.endDate),
  }));

  const courseFilterOptions = Array.isArray(allConfiguredCourses)
    ? allConfiguredCourses.map((c) => ({
        label: c.courseNameSnapshot,
        value: c.id,
        id: c.id,
      }))
    : [];

  return (
    <Box padding={2}>
      <Box display="flex" justifyContent="flex-end" mb={2} gap={2}>
        <LoadingButton
          variant="outlined"
          startIcon={<SyncIcon />}
          loading={isAutoLinking}
          onClick={() => autoLinkMappings()}
        >
          Moodle Mapping
        </LoadingButton>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setExpanded((prev) => !prev)}
        >
          Create Mandatory Course
        </Button>
      </Box>

      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{ mb: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>New Mandatory Course</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Year"
                  fullWidth
                  disabled
                  error={!!errors.year}
                  helperText={errors.year?.message}
                  {...register('year')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Moodle Course
                </Typography>
                <Controller
                  name="moodleCourseId"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      inputId="moodleCourseId"
                      options={courseOptions}
                      isLoading={coursesLoading}
                      placeholder="Search course..."
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      value={
                        courseOptions.find((o) => o.value === field.value) ??
                        null
                      }
                      onChange={(selected) => {
                        field.onChange(selected?.value ?? null);
                        setValue('courseNameSnapshot', selected?.label ?? '');
                        setValue('launchDate', selected?.startDate ?? '');
                        setValue('endDate', selected?.endDate ?? '');
                      }}
                    />
                  )}
                />
                {errors.moodleCourseId && (
                  <FormHelperText error>
                    {errors.moodleCourseId.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Launch Date"
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.launchDate}
                  helperText={
                    errors.launchDate?.message ??
                    'Auto-filled from selected course'
                  }
                  {...register('launchDate')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="End Date"
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.endDate}
                  helperText={
                    errors.endDate?.message ??
                    'Auto-filled from selected course'
                  }
                  {...register('endDate')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Grace Days"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  error={!!errors.graceDays}
                  helperText={errors.graceDays?.message}
                  {...register('graceDays')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Freeze Days"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  error={!!errors.freezeDays}
                  helperText={errors.freezeDays?.message}
                  {...register('freezeDays')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.recurrence}>
                  <InputLabel>Recurrence</InputLabel>
                  <Controller
                    name="recurrence"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Recurrence">
                        {RECURRENCE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.recurrence && (
                    <FormHelperText>{errors.recurrence.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="warningNotificationDays"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={field.value.map(String)}
                      disabled={
                        watch('graceDays') === 0 || watch('graceDays') === '0'
                      }
                      onChange={(e, newValue) => {
                        const nums = newValue
                          .map((v) => parseInt(v, 10))
                          .filter((n) => !isNaN(n));
                        field.onChange([...new Set(nums)]);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Warning Notification Days"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="completionCcEmails"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={field.value || []}
                      onChange={(e, newValue) => field.onChange(newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Completion CC Emails"
                          error={!!errors?.completionCcEmails}
                          helperText={
                            errors?.completionCcEmails
                              ? 'Please enter valid email(s)'
                              : ''
                          }
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <Controller
                  name="applicableLocationIds"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      filterSelectedOptions
                      options={locations}
                      loading={locationsLoading}
                      getOptionLabel={(option) => option.locationName || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={(field.value || [])
                        .map((id) => locations.find((loc) => loc.id === id))
                        .filter(Boolean)}
                      onChange={(e, newValue) => {
                        field.onChange(newValue.map((loc) => loc.id));
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.locationName}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Applicable Locations"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isMandatory"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Mandatory"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Active"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isPending}
                >
                  Save Course
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </AccordionDetails>
      </Accordion>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 4 }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {COURSE_TABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableLoading ? (
              <TableRow>
                <TableCell colSpan={COURSE_TABLE_COLUMNS.length} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : Array.isArray(configuredCourses) &&
              configuredCourses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COURSE_TABLE_COLUMNS.length} align="center">
                  No mandatory courses configured yet.
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(configuredCourses) &&
              configuredCourses.map((row, index) => (
                <TableRow key={row.id ?? index} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.courseNameSnapshot}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>{row.year}</TableCell>
                  <TableCell sx={{ px: 2 }}>{row.recurrence}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.launchDate}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.endDate}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>{row.graceDays ?? 0}</TableCell>
                  <TableCell sx={{ px: 2 }}>{row.freezeDays ?? 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.isMandatory ? 'Yes' : 'No'}
                      color={row.isMandatory ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.isActive ? 'Active' : 'Inactive'}
                      color={row.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>
                    {row.warningNotificationDays?.join(', ') || '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => setEditingCourse(row)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <PaginationComponent
          count={totalPages || 0}
          page={page + 1}
          pageSize={pageSize}
          pageChange={(event, value) => setPage(value - 1)}
          handlePageSizeChange={(event) => {
            setPageSize(event.target.value);
            setPage(0);
          }}
          paginationTitle="Rows per page"
        />
      </Box>

      {/* Exclusions Section */}
      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6" fontWeight={600}>
            Course Exclusions
          </Typography>
          <Box minWidth={260}>
            <ReactSelect
              options={courseFilterOptions}
              placeholder="Filter by course..."
              value={
                selectedCourseForExclusions
                  ? (courseFilterOptions.find(
                      (o) => o.id === selectedCourseForExclusions.id,
                    ) ?? null)
                  : null
              }
              onChange={(opt) =>
                setSelectedCourseForExclusions(opt ? { id: opt.id } : null)
              }
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<BlockIcon />}
          onClick={() => setExcludeModalOpen(true)}
        >
          Exclude Users
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              {EXCLUSION_TABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!selectedCourseForExclusions ? (
              <TableRow>
                <TableCell
                  colSpan={EXCLUSION_TABLE_COLUMNS.length}
                  align="center"
                >
                  Select a course above to view its exclusions.
                </TableCell>
              </TableRow>
            ) : exclusionsLoading ? (
              <TableRow>
                <TableCell
                  colSpan={EXCLUSION_TABLE_COLUMNS.length}
                  align="center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : exclusions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={EXCLUSION_TABLE_COLUMNS.length}
                  align="center"
                >
                  No exclusions found for this course.
                </TableCell>
              </TableRow>
            ) : (
              exclusions.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ px: 2 }}>{row.employeeCode}</TableCell>
                  <TableCell sx={{ px: 2 }}>{row.moodleCourseId}</TableCell>
                  <TableCell sx={{ px: 2 }}>{row.reason}</TableCell>
                  <TableCell sx={{ px: 1 }} align="right">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isRemoving}
                      onClick={() =>
                        removeExclusion({
                          courseConfigId: selectedCourseForExclusions.id,
                          employeeId: row.employeeId,
                        })
                      }
                    >
                      {isRemoving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ExcludeUsersModal
        open={excludeModalOpen}
        onClose={() => setExcludeModalOpen(false)}
        configuredCourses={configuredCourses || []}
      />

      <EditCourseModal
        open={!!editingCourse}
        onClose={() => setEditingCourse(null)}
        course={editingCourse}
      />
    </Box>
  );
};

export default Courses;
